import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { freemiumAPI } from '../lib/api';
import api from '../lib/api';

const FreemiumContext = createContext(null);

// Códigos backend que disparan el modal
const BLOCK_CODES = new Set(['freemium_limit_reached', 'trial_gratuito_finalizado', 'sin_creditos_ia', 'sin_mejoras_ia', 'trial_expired']);

export function FreemiumProvider({ children }) {
    const { isAuthenticated, loading: authLoading, user } = useAuth();
    const [status, setStatus] = useState(null);
    const [modal, setModal] = useState({ open: false, payload: null });
    const interceptorId = useRef(null);

    const fetchStatus = useCallback(async () => {
        if (!isAuthenticated) {
            setStatus(null);
            return null;
        }
        try {
            const r = await freemiumAPI.getStatus();
            setStatus(r.data);
            return r.data;
        } catch (_) {
            setStatus(null);
            return null;
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (!authLoading) fetchStatus();
    }, [authLoading, fetchStatus, user?.id]);

    // Interceptor global de respuestas: al detectar HTTP 402 con code freemium/legacy → abre modal
    useEffect(() => {
        if (interceptorId.current !== null) return;
        interceptorId.current = api.interceptors.response.use(
            (r) => r,
            (error) => {
                const detail = error?.response?.data?.detail;
                if (error?.response?.status === 402 && detail && BLOCK_CODES.has(detail.code)) {
                    setModal({ open: true, payload: detail });
                    // refresca status para reflejar contadores
                    fetchStatus();
                }
                return Promise.reject(error);
            }
        );
        // no eject en unmount: es global durante toda la app
    }, [fetchStatus]);

    const openModal = useCallback((payload = null) => setModal({ open: true, payload }), []);
    const closeModal = useCallback(() => setModal({ open: false, payload: null }), []);

    const value = {
        status,
        loading: false,
        refresh: fetchStatus,
        isAdmin: !!status?.is_admin,
        unlimitedAccess: !!status?.unlimited_access,
        modo: status?.modo || null,
        planActivo: !!status?.plan_activo,
        accesoCompleto: status ? !!status.acceso_completo : true,
        puedeCrearBorrador: status ? !!status.puede_crear_borrador : true,
        puedeAnalizarIA: status ? !!status.puede_analizar_ia : true,
        puedeGenerarDiagnostico: status ? !!status.puede_generar_diagnostico : true,
        puedeVerStats: status ? !!status.puede_ver_stats : true,
        counters: status?.counters || {},
        freeIaCredits: status?.free_ia_credits ?? null,
        freeIaCreditsMax: status?.free_ia_credits_max ?? null,
        cutoffIso: status?.cutoff_iso || null,
        cutoffPasado: !!status?.cutoff_pasado,
        legacyCreditsCutoffIso: status?.legacy_credits_cutoff_iso || null,
        limites: status?.limites || null,
        modal,
        openModal,
        closeModal,
    };

    return <FreemiumContext.Provider value={value}>{children}</FreemiumContext.Provider>;
}

export function useFreemium() {
    const ctx = useContext(FreemiumContext);
    if (!ctx) {
        return {
            status: null, loading: false, refresh: () => {}, isAdmin: false,
            unlimitedAccess: false, modo: null,
            planActivo: false, accesoCompleto: true, puedeCrearBorrador: true,
            puedeAnalizarIA: true, puedeGenerarDiagnostico: true, puedeVerStats: true,
            counters: {}, freeIaCredits: null, freeIaCreditsMax: null,
            cutoffIso: null, cutoffPasado: false, legacyCreditsCutoffIso: null, limites: null,
            modal: { open: false, payload: null }, openModal: () => {}, closeModal: () => {},
        };
    }
    return ctx;
}
